import { FLOW_ATTRIBUTES, useFlowHeaders } from "#hooks/tables/useFlowHeaders";
import { useNetworkCalculationSourceHeaders } from "#routes/organization/networks/[networkId]/detail/NetworkCalculationTab/NetworkCalculationTab.helpers";
import { useNetworkCalculationResult } from "#routes/organization/networks/[networkId]/detail/calculation-result/[period]/details/NetworkCalculationResultPage.helpers";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import {
  DataTable,
  DataTablePanel,
  HeaderType,
} from "@validereinc/common-components";
import {
  CalculationOutputType,
  FlowType,
  FlowV2Type,
} from "@validereinc/domain";
import React from "react";

export const CalculationFlowVariant = {
  INLET: "inlet",
  ADJUSTABLE: "adjustable",
  NON_ADJUSTABLE: "non_adjustable",
};

export type CalculationFlowVariantType =
  (typeof CalculationFlowVariant)[keyof typeof CalculationFlowVariant];

export const NetworkCalculationResultFlowPanel = ({
  variant,
}: {
  variant: CalculationFlowVariantType;
}) => {
  const { getUnitName, isLoading: isLoadingMeasurementTypes } =
    useMeasurementTypes();
  const {
    network,
    flows,
    configuration,
    run,
    isLoading: isLoadingNetworkCalculationResult,
  } = useNetworkCalculationResult();
  const { isLoading: isLoadingFlowHeaders, headers: flowHeaders } =
    useFlowHeaders();
  const {
    isLoading: isLoadingNetworkCalculationSourceHeaders,
    headers: networkCalculationSourceHeaders,
  } = useNetworkCalculationSourceHeaders<FlowV2Type>(configuration);

  const isAdjustable = variant === CalculationFlowVariant.ADJUSTABLE;

  const headers: Array<HeaderType<FlowType>> = [
    ...flowHeaders.filter(({ key }) =>
      [FLOW_ATTRIBUTES.NAME.key, FLOW_ATTRIBUTES.TYPE.key].includes(key)
    ),
    ...networkCalculationSourceHeaders,
    ...(isAdjustable
      ? [
          {
            key: "volume_adjusted",
            label: "Adjusted Volume",
            renderComponent: ({ item }: { item: FlowV2Type }) => {
              const input = (
                run?.output?.outputs as Array<
                  CalculationOutputType & { asset_id: string }
                >
              )?.find(
                ({ measurement_type, asset_id }) =>
                  measurement_type === "volume" && asset_id === item.id
              );
              return input ? (
                <DataTable.DataRow.NumberCell
                  value={input.measurement_value}
                  unit={getUnitName(input.measurement_unit)}
                />
              ) : (
                "-"
              );
            },
          },
        ]
      : []),
  ];

  const items = flows.filter(({ id }) => {
    switch (variant) {
      case CalculationFlowVariant.INLET:
        // @ts-expect-error TODO: Typing on assetGroup
        return network?.network_inlet_ids?.includes(id);
      default:
        return (
          // @ts-expect-error TODO: Typing on assetGroup
          !network?.network_inlet_ids?.includes(id) &&
          (run as any)?.configuration_asset_input[id]?.is_prorated?.value ==
            isAdjustable
        );
    }
  });

  const title =
    variant === CalculationFlowVariant.INLET
      ? "Inlet Flows"
      : variant === CalculationFlowVariant.ADJUSTABLE
        ? "Adjustable Flows"
        : "Non Adjustable Flows";

  return (
    <DataTablePanel
      panelProps={{ title, isFluidY: false }}
      dataTableProps={{
        items: items as FlowType[],
        getItemId: (item) => item.id,
        headers,
        isLoading:
          isLoadingMeasurementTypes ||
          isLoadingNetworkCalculationResult ||
          isLoadingFlowHeaders ||
          isLoadingNetworkCalculationSourceHeaders,
      }}
    />
  );
};
